import {
  Design,
  Draft,
  DraftBendingTextConfiguration,
  DraftConfiguration,
  DraftConfigurationBase,
  DraftConfigurationFilter,
  DraftConfigurationMask,
  DraftConfigurationText,
  DraftDesignConfiguration,
  DraftPlaceholderTextConfiguration,
  DraftSpecialTextConfiguration,
  DraftTextConfiguration,
  Mask,
  PrintType,
  PrintTypesMap,
  ProductType,
  SharingDraft,
  SharingDraftConfiguration,
  SharingDraftDesignConfiguration,
  Size,
  SizePx,
  SpecialFontTask,
  StoreFont,
  StoreFontFamily,
  StoreSpecialFont,
} from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { assertNever } from '@/utils/assertNever';
import { getConfigurationMaskObjectUrl } from '@/utils/masks/getConfigurationMaskObjectUrl';
import { getMaskedDesignPreview } from '@/utils/masks/getMaskedDesignPreview';
import { updateMaskVersion } from '@/utils/masks/updateMaskVersion';
import { trackError } from '@/utils/raygun';
import { getBendingTextSize } from '@/utils/text/getBendingTextSize';
import { getTextSizeForStyle } from '@/utils/text/getTextSizeForStyle';
import { sanitizeText } from '@/utils/text/sanitizeText';
import { textAnchorToAlign } from '@/utils/text/textAnchorToAlign';
import {
  getConfigurationFilterObjectUrl,
  getFilterPresets,
  initFilterPreview,
} from '@/features/filters/filters';
import { convertSizePxToSizeMm, ensureSizeMm } from '@/model/configuration/convertSize';
import { getAdjustedOffset } from '@/model/configuration/getAdjustedScaleAndOffset';
import { getDraftConfigurationRestrictForDesign } from '@/model/configuration/getDraftConfigurationRestrictForDesign';
import { getMaxScale } from '@/model/configuration/getMaxScale';
import { getRandomCid } from '@/model/configuration/getRandomCid';
import { scaleSize } from '@/model/configuration/scaleSize';
import { isPixelDesign } from '@/model/design/isDesignType';
import { assertPixelDesign } from '@/model/design/isDesignType';
import { getRotationFromTransform } from '@/model/draftMapper/utils/getRotationFromTransform';
import { getSpecialFontTask as getLoadedSpecialFontTask } from '@/model/font/fetchSpecialFontTask';
import { upgradePrintAreas } from '@/model/printArea/upgradePrintAreas';
import { getAppearance } from '@/model/productType/getAppearance';
import { getDefaultView } from '@/model/productType/getDefaultView';
import { getPrintArea } from '@/model/productType/getPrintArea';
import { getPrintTypesOfAppearance } from '@/model/productType/getPrintTypesOfAppearance';
import { getView } from '@/model/productType/getView';
import { rotatedSize } from '../configuration/rotatedSize';
import { getPrintColors } from './utils/getPrintColors';
import { getPrintType } from './utils/getPrintType';
import {
  isSharedConfigurationFilterWithId,
  isSharingDraftBendingTextConfiguration,
  isSharingDraftConfigurationWithText,
  isSharingDraftDesignConfiguration,
  isSharingDraftPlaceholderTextConfiguration,
  isSharingDraftSpecialTextConfiguration,
  isSharingDraftTextConfiguration,
} from './utils/isSharingDraftConfigurationType';

type DraftConfigurationBaseWithoutSize = Omit<DraftConfigurationBase, 'size'>;

interface ImageEffectProperties {
  cid: number;
  filter?: DraftConfigurationFilter;
  mask?: DraftConfigurationMask;
  processedImage?: string;
  processedSize?: SizePx;
}

/**
 * prevents validation errors by
 *  observing the 1mm reduction of the printable area - important for products mapped from sellables
 *  resizing the design if the DPI of the print type has changed
 * can adjust draftConfigurationBase.offset
 */
const considerMaxScale = (
  draftConfigurationBase: DraftConfigurationBaseWithoutSize,
  scale: number,
  size: Size,
  isPixelConfiguration = true
) => {
  const printArea = draftConfigurationBase.printArea;
  const printType = draftConfigurationBase.printType;

  const maxScale = getMaxScale(
    size,
    draftConfigurationBase.rotation,
    printArea,
    printType,
    isPixelConfiguration
  );

  if (maxScale < scale) {
    draftConfigurationBase.offset = getAdjustedOffset(
      draftConfigurationBase.offset,
      scaleSize(size, scale),
      printArea,
      scaleSize(size, maxScale)
    );

    return maxScale;
  }

  return scale;
};

const configurationsToReduxModel = (
  sharingDraftConfigurations: SharingDraftConfiguration[],
  productType: ProductType,
  availablePrintTypes: PrintType[],
  allPrintTypes: PrintTypesMap,
  masks: Mask[],
  specialFonts: StoreSpecialFont[],
  designMap: Map<string, Design>,
  fontMap: Map<string, StoreFont>,
  imageEffectMap: Map<SharingDraftConfiguration, ImageEffectProperties>
) =>
  sharingDraftConfigurations.map((sharingDraftConfiguration) => {
    const imageEffect = imageEffectMap.get(sharingDraftConfiguration);
    const cid = imageEffect ? imageEffect.cid : getRandomCid();
    const printType = getPrintType(availablePrintTypes, allPrintTypes, sharingDraftConfiguration);
    const draftConfigurationBase: DraftConfigurationBaseWithoutSize = {
      cid,
      printArea: assertDefined(getPrintArea(productType, sharingDraftConfiguration.printAreaId)),
      printType,
      printColors: getPrintColors(sharingDraftConfiguration, printType),
      offset: { ...sharingDraftConfiguration.offset },
      rotation: getRotationFromTransform(sharingDraftConfiguration.transform),
      scale: 1,
    };

    if (isSharingDraftDesignConfiguration(sharingDraftConfiguration)) {
      const design = assertDefined(designMap.get(sharingDraftConfiguration.designId));
      const designSize = ensureSizeMm(imageEffect?.processedSize || design.size, printType);
      draftConfigurationBase.scale = considerMaxScale(
        draftConfigurationBase,
        sharingDraftConfiguration.width / designSize.width,
        designSize,
        isPixelDesign(design)
      );

      const draftDesignConfiguration: DraftDesignConfiguration = {
        type: sharingDraftConfiguration.type,
        ...draftConfigurationBase,
        design,
        size: ensureSizeMm(design.size, printType),
        restrict: getDraftConfigurationRestrictForDesign(design),
      };

      if (imageEffect) {
        const { filter, mask, processedImage, processedSize } = imageEffect;

        if (filter) {
          draftDesignConfiguration.filter = filter;
        }

        if (mask && processedSize) {
          draftDesignConfiguration.mask = mask;
          draftDesignConfiguration.processedSize = processedSize;
          draftDesignConfiguration.size = convertSizePxToSizeMm(processedSize, printType);
        }

        draftDesignConfiguration.processedImage = processedImage;
      }

      return draftDesignConfiguration;
    }

    if (isSharingDraftConfigurationWithText(sharingDraftConfiguration)) {
      const isBendingTextConfiguration =
        isSharingDraftBendingTextConfiguration(sharingDraftConfiguration);
      const text: DraftConfigurationText = {
        content: sanitizeText(sharingDraftConfiguration.text),
        style: {
          font: assertDefined(
            fontMap.get(
              `${sharingDraftConfiguration.fontFamilyId}|${sharingDraftConfiguration.fontId}`
            )
          ),
          fontSize: sharingDraftConfiguration.fontSize || 25,
          textAlign: isBendingTextConfiguration
            ? 'left'
            : textAnchorToAlign(sharingDraftConfiguration.textAnchor),
          angle: isBendingTextConfiguration ? sharingDraftConfiguration.angle : 0,
          x: isBendingTextConfiguration ? 0 : sharingDraftConfiguration.x,
          y: isBendingTextConfiguration ? 0 : sharingDraftConfiguration.y,
        },
      };
      const textSize = getTextSizeForStyle(text.style, text.content);

      if (isSharingDraftTextConfiguration(sharingDraftConfiguration)) {
        let offsetWidth = textSize.width;
        let offsetHeight = textSize.height;

        const rotation = draftConfigurationBase.rotation;
        if (rotation) {
          const rotatedTextSize = rotatedSize(rotation, textSize);
          offsetWidth = rotatedTextSize.width / 2;
          offsetHeight = rotatedTextSize.height / 2;
        }

        const draftTextConfiguration: DraftTextConfiguration = {
          type: sharingDraftConfiguration.type,
          ...draftConfigurationBase,
          offset: {
            x: Math.min(
              draftConfigurationBase.offset.x,
              draftConfigurationBase.printArea.boundary.size.width - offsetWidth
            ),
            y: Math.min(
              draftConfigurationBase.offset.y,
              draftConfigurationBase.printArea.boundary.size.height - offsetHeight
            ),
          },
          text,
          size: textSize,
        };

        return draftTextConfiguration;
      }

      if (isSharingDraftPlaceholderTextConfiguration(sharingDraftConfiguration)) {
        const draftPlaceholderTextConfiguration: DraftPlaceholderTextConfiguration = {
          type: sharingDraftConfiguration.type,
          ...draftConfigurationBase,
          text,
          size: textSize,
          placeholderSize: sharingDraftConfiguration.placeholderSize,
          placeholderCenter: {
            x: sharingDraftConfiguration.offset.x + sharingDraftConfiguration.width / 2,
            y: sharingDraftConfiguration.offset.y + sharingDraftConfiguration.height / 2,
          },
          placeholderFontSize: sharingDraftConfiguration.fontSize || 25,
        };

        return draftPlaceholderTextConfiguration;
      }

      if (isSharingDraftBendingTextConfiguration(sharingDraftConfiguration)) {
        const textSize = getBendingTextSize(text);
        draftConfigurationBase.scale = sharingDraftConfiguration.width / textSize.width;

        const draftBendingTextConfiguration: DraftBendingTextConfiguration = {
          type: sharingDraftConfiguration.type,
          ...draftConfigurationBase,
          text,
          size: textSize,
        };

        return draftBendingTextConfiguration;
      }
    }

    if (isSharingDraftSpecialTextConfiguration(sharingDraftConfiguration)) {
      const task = assertDefined(
        getLoadedSpecialFontTask(
          sharingDraftConfiguration.fontId,
          sharingDraftConfiguration.textAlign,
          sharingDraftConfiguration.text
        )
      );
      const taskSize = convertSizePxToSizeMm(task.size, printType);

      draftConfigurationBase.scale = considerMaxScale(
        draftConfigurationBase,
        sharingDraftConfiguration.width / taskSize.width,
        taskSize
      );

      const draftSpecialTextConfiguration: DraftSpecialTextConfiguration = {
        type: sharingDraftConfiguration.type,
        ...draftConfigurationBase,
        text: {
          content: sanitizeText(sharingDraftConfiguration.text),
          style: {
            font: assertDefined(
              specialFonts.find((font) => font.id === sharingDraftConfiguration.fontId)
            ),
            textAlign: sharingDraftConfiguration.textAlign,
          },
        },
        task,
        size: convertSizePxToSizeMm(task.size, printType),
      };

      return draftSpecialTextConfiguration;
    }

    return assertNever(sharingDraftConfiguration);
  });

type GettersForMapDraft = {
  getProductType: (productTypeId: string) => Promise<ProductType>;
  getDesign: (designId: string) => Promise<Design>;
  getLoadedFont: (font: StoreFont) => Promise<StoreFont>;
  getSpecialFontTask: (fontId: string, align: string, text: string) => Promise<SpecialFontTask>;
};

export const mapSharingDraftToDraft = async (
  draft: SharingDraft,
  allPrintTypes: PrintTypesMap,
  fontFamilies: StoreFontFamily[],
  specialFonts: StoreSpecialFont[],
  masks: Mask[],
  { getProductType, getDesign, getLoadedFont, getSpecialFontTask }: GettersForMapDraft
): Promise<Draft> => {
  if (
    specialFonts.length === 0 &&
    draft.configurations.some(isSharingDraftSpecialTextConfiguration)
  ) {
    throw new Error('Special fonts have been disabled');
  }

  const designMap = new Map<string, Design>();
  const designPromiseMap = new Map<string, Promise<Design>>();
  const designPromises = draft.configurations
    .filter(isSharingDraftDesignConfiguration)
    .map(async (configuration) => {
      const designId = configuration.designId;

      if (designPromiseMap.has(designId)) {
        return;
      }

      let designPromise = getDesign(designId);

      const productDesignId = configuration.productDesignId;
      if (productDesignId) {
        designPromise = designPromise.catch(() => {
          console.warn(
            `Failed to fetch original design (${designId}) of mask/filter design (${productDesignId})`
          );
          return getDesign(productDesignId);
        });
      }

      designPromiseMap.set(designId, designPromise);

      const design = await designPromise;
      designMap.set(designId, design);
    });

  const fontMap = new Map<string, StoreFont>();
  const fontPromises = draft.configurations
    .filter(isSharingDraftConfigurationWithText)
    .map(async (configuration) => {
      const fontFamily = assertDefined(
        fontFamilies.find((f) => f.id === configuration.fontFamilyId)
      );
      let font = fontFamily.fonts.find((f) => f.id === configuration.fontId);
      if (!font) {
        trackError('Invalid text config', { draft, config: configuration, fontFamily });
        font = fontFamily.fonts[0];
        configuration.fontId = font.id;
      }

      // we need this key instead of the fontIdentifier, because in the next mapping step we only have these two ids
      const fontKey = `${configuration.fontFamilyId}|${configuration.fontId}`;
      if (fontMap.has(fontKey)) {
        return;
      }

      // add to map, so we don't load the same font twice
      fontMap.set(fontKey, {} as StoreFont);
      const loadedFont = await getLoadedFont(font);
      fontMap.set(fontKey, loadedFont);
    });

  const specialFontTaskPromises = draft.configurations
    .filter(isSharingDraftSpecialTextConfiguration)
    .map((configuration) =>
      getSpecialFontTask(configuration.fontId, configuration.textAlign, configuration.text)
    );

  const imageEffectMap = new Map<SharingDraftConfiguration, ImageEffectProperties>();
  const imageEffectPromises = draft.configurations
    .filter(
      (configuration): configuration is SharingDraftDesignConfiguration =>
        isSharingDraftDesignConfiguration(configuration) &&
        Boolean(configuration.filter || configuration.mask)
    )
    .map(async (configuration) => {
      const cid = getRandomCid();
      let configurationFilter: DraftConfigurationFilter | undefined;
      let configurationMask: DraftConfigurationMask | undefined;
      let processedImage: string | undefined;
      let processedSize: SizePx | undefined;

      const sharedConfigurationFilter = configuration.filter;
      let sharedConfigurationMask = configuration.mask;

      if (isSharedConfigurationFilterWithId(sharedConfigurationFilter)) {
        const filterId = sharedConfigurationFilter.id;
        const presets = await getFilterPresets();
        const preset = presets.find(({ id }) => id === filterId);
        if (preset) {
          configurationFilter = {
            settings: preset.settings,
            designId: sharedConfigurationFilter.designId,
          };
        }
      } else if (sharedConfigurationFilter) {
        configurationFilter = sharedConfigurationFilter;
      }

      if (configurationFilter) {
        await initFilterPreview(
          cid,
          configuration.designId,
          configurationFilter.settings,
          configurationFilter.designId
        );

        if (!sharedConfigurationMask) {
          processedImage = assertDefined(getConfigurationFilterObjectUrl(cid));
        }
      }

      if (sharedConfigurationMask) {
        const design = assertDefined(await designPromiseMap.get(configuration.designId));

        if (design.id === configuration.productDesignId) {
          // if the loaded design is the productDesign, show the productDesign without any image effects
          return;
        }

        const maskId = sharedConfigurationMask.id;
        const mask = assertDefined(masks.find((m) => m.id === maskId));

        if (!sharedConfigurationMask.version) {
          sharedConfigurationMask = await updateMaskVersion(sharedConfigurationMask, mask, design);
        }

        const designConfigurationScale = { ...sharedConfigurationMask.scale };
        const designConfigurationOffset = { ...sharedConfigurationMask.offset };
        configurationMask = {
          ...mask,
          scale: designConfigurationScale,
          offset: designConfigurationOffset,
          version: sharedConfigurationMask.version!,
        };

        [processedSize] = await getMaskedDesignPreview(
          mask,
          cid,
          assertPixelDesign(design),
          designConfigurationScale,
          designConfigurationOffset
        );
        processedImage = getConfigurationMaskObjectUrl(cid);
      }

      imageEffectMap.set(configuration, {
        cid,
        filter: configurationFilter,
        mask: configurationMask,
        processedImage,
        processedSize,
      });
    });

  const productTypePromise = getProductType(draft.productTypeId);

  const [productType] = await Promise.all<any>([
    productTypePromise,
    ...designPromises,
    ...fontPromises,
    ...specialFontTaskPromises,
    ...imageEffectPromises,
  ]);

  const appearance = assertDefined(
    getAppearance(productType, draft.appearanceId) || getAppearance(productType)
  );

  const availablePrintTypes = getPrintTypesOfAppearance(appearance);

  const configurations = configurationsToReduxModel(
    draft.configurations,
    productType,
    availablePrintTypes,
    allPrintTypes,
    masks,
    specialFonts,
    designMap,
    fontMap,
    imageEffectMap
  );

  upgradePrintAreas(configurations, productType, appearance);

  return {
    productType,
    appearance,
    view: getView(productType, draft.viewId) || getDefaultView(productType),
    configurations: configurations.reduce<Record<string, DraftConfiguration>>(
      (map, configuration) => {
        map[String(configuration.cid)] = configuration;
        return map;
      },
      {}
    ),
    configurationIds: configurations.map(({ cid }) => cid),
  };
};
